import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";

import { useFlags } from "launchdarkly-react-client-sdk";

import Header from "components/Header/Header";
import PaymentHistory from "./PaymentHistory";
import Users from "./Users";
import Overview from "./Overview";
import CustomerProfile from "./CustomerProfile";
import PaymentDetails from "./PaymentDetails";
import { getValue } from "components/Screens/CommonUtils";

import PageLoader from "components/common/PageLoader";

import { GetCustomerDetails } from "store/actions/Customers/GetCustomerDetails";
import { ResetGetCustomerPaymentSummary } from "store/actions/Customers/GetCustomerPaymentSummary";
import { ResetGetCustomerPaymentById } from "store/actions/Customers/GetCustomerPaymentById";
import PageHeader from "components/Screens/PageHeader";
import UserList from "./UserList";

const headersWithoutCustomerPaymentDetails = ["Profile", "Overview", "Payment History", "Users"];

function ComponentMapper({
  option,
  selectPageHeading,
  getSelectedPaymentId,
  customerId,
  selectedPaymentId,
  selectedPaymentHistoryRow,
  featureFlag,
}) {
  const handleNavigateToPaymentHistory = () => {
    selectPageHeading("Payment History");
  };

  const components = {
    Profile: <CustomerProfile />,
    Overview: <Overview onNavigateToPaymentHistory={handleNavigateToPaymentHistory} />,
    "Payment History": (
      <PaymentHistory selectPageHeading={selectPageHeading} getSelectedPaymentId={getSelectedPaymentId} />
    ),
    Users: featureFlag ? <UserList customerId={customerId} /> : <Users customerId={customerId} />,
    "Payment Details": (
      <PaymentDetails
        selectedPaymentId={selectedPaymentId}
        selectedPaymentHistoryRow={selectedPaymentHistoryRow}
        customerId={customerId}
      />
    ),
  };

  return components[option] || null;
}

function CustomerDetails() {
  const params = useParams();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [option, selectedOption] = useState("Profile");
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [selectedPaymentHistoryRow, setSelectedPaymentHistoryRow] = useState(null);

  const customerDetails = useSelector((globalState) => globalState.GetCustomerDetails.customerDetails);
  const loadingCustomerDetails = useSelector((globalState) => globalState.GetCustomerDetails.loadingCustomerDetails);
  const loadingCustomerPaymentById = useSelector(
    (globalState) => globalState.GetCustomerPaymentById.loadingCustomerPaymentById
  );

  const customerId = customerDetails?.payload?.customerId;

  const flags = useFlags();

  const selectPageHeading = (opt) => {
    if (opt != "Payment Details") {
      dispatch(ResetGetCustomerPaymentById({}));
    }
    selectedOption(opt);
  };

  const getSelectedPaymentId = (paymentId, paymentHistoryRow) => {
    if (paymentId != undefined || paymentId != null) {
      setSelectedPaymentId(paymentId);
      setSelectedPaymentHistoryRow(paymentHistoryRow);
    }
  };

  useEffect(() => {
    if (params.id) {
      dispatch(ResetGetCustomerPaymentSummary({}));
      if (!Object.keys(customerDetails).length || params.id !== customerId || state?.redirectFrom === "editCustomer") {
        dispatch(
          GetCustomerDetails({
            customerId: params.id,
          })
        );
      }
    }
  }, [params.id, state?.redirectFrom, customerId]);

  return (
    <>
      <Helmet>
        <title>{`Edge | Customers - ${getValue(customerDetails?.payload?.companyName)}`}</title>
      </Helmet>
      {(loadingCustomerDetails || loadingCustomerPaymentById) && <PageLoader />}
      <Grid container direction="column" rowSpacing={3}>
        <Header
          data={{
            heading: getValue(customerDetails?.payload?.companyName),
            subHeader: null,
            letterSpacing: "15px",
            profileWithIcon: null,
          }}
        />
        <PageHeader
          pageHeadings={headersWithoutCustomerPaymentDetails}
          selectPageHeading={selectPageHeading}
          selectedOption={option}
        />

        <Grid item container direction="row">
          <Grid item xs={12}>
            <ComponentMapper
              option={option}
              selectPageHeading={selectPageHeading}
              getSelectedPaymentId={getSelectedPaymentId}
              customerId={customerId}
              selectedPaymentId={selectedPaymentId}
              selectedPaymentHistoryRow={selectedPaymentHistoryRow}
              featureFlag={flags?.teamManagement}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default CustomerDetails;
